import { useEffect, useRef } from "react";
import styles from "./SectionHeroTitle.module.css";
import BrandStripeChrome from "../BrandStripeChrome/BrandStripeChrome";

/**
 * Hardcoded preview image for step 1;same hero on NoticeOne, Music Catalog,
 * and About so you can compare layout/scroll. Replace with CMS props later.
 * Straight (orthographic) art; perspective comes from CSS 3D on the plane.
 */
export const SECTION_HERO_PREVIEW_IMAGE =
  "https://res.cloudinary.com/do0dxcsdm/image/upload/v1785259086/3d-image-v5.4.png";

// Keep in sync with SectionHeroTitle.module.css keyframes / reduced-motion
/** Vanishing-plane tilt (deg). Open near edge → recedes into distance. */
const SCROLL_TILT_FROM_DEG = 52;
const SCROLL_TILT_TO_DEG = 78;
const SCROLL_TILT_REDUCED_DEG = 58;
/*
  Plane width: CSS keyframes use scale(1, 1) on X (see SectionHeroTitle.module.css
  @keyframes sectionHeroScrollTilt + ROLLBACK block). JS fallback only sets
  rotateX; it never applied the old scale(2, …), so no scale change needed here.
*/

const supportsViewTimeline =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("animation-timeline", "view()");

/** Named dark overlays on top of the hero image (independent of opacity). */
const OVERLAY_CLASS = {
  none: "",
  light: styles["overlay-light"],
  medium: styles["overlay-medium"],
  dark: styles["overlay-dark"],
  "very-dark": styles["overlay-very-dark"],
  verydark: styles["overlay-very-dark"],
};

/**
 * Shared section hero: scroll-linked image + arch title plate (NoticeOne look).
 *
 * Hero art is a flat image on a CSS 3D plane (perspective + rotateX). When
 * showImageUnderside is on, a second sibling plane (same motion) is added lower
 * — the front plane is unchanged vs flag-off.
 *
 * Scroll progress is measured against the short title root; not the tall
 * absolute image; so About on tall screens does not jump to an extreme pose
 * just because the plane’s paint box already fills the viewport.
 *
 * @param {{
 *   title: string,
 *   imageUrl?: string,
 *   imageAlt?: string,
 *   headingLevel?: 2 | 3,
 *   titleClassName?: string,
 *   className?: string,
 *   imageOpacity?: number,
 *   imageOpacityMobile?: number,
 *   imageOpacityUnderside?: number,
 *   imageOpacityUndersideMobile?: number,
 *   imageOverlay?: "none" | "light" | "medium" | "dark" | "very-dark" | boolean,
 *   imageBrightness?: number,
 *   showBottomBorder?: boolean,
 *   showImageUnderside?: boolean,
 * }} props
 *
 * imageOpacity; top plane figure opacity 0–1 (default 0.5, NoticeOne legacy).
 * imageOpacityMobile; top plane opacity on mobile (max-width 600px).
 *   Defaults to imageOpacity if omitted.
 * imageOpacityUnderside; lower plane opacity when showImageUnderside is on.
 *   Defaults to imageOpacity if omitted.
 * imageOpacityUndersideMobile; lower plane opacity on mobile.
 *   Defaults to imageOpacityUnderside if omitted (then imageOpacityMobile / imageOpacity).
 * imageOverlay; dark wash masked to the image alpha (angled transparent edges stay clear).
 *   true ≡ "medium".
 * imageBrightness; CSS brightness() on the photo only (1 = normal, 0.6 = darker). Also alpha-safe.
 * showBottomBorder; accent red rule under the hero (default true). Pass false to omit.
 * showImageUnderside; adds a second plane with the same image + scroll motion,
 *   offset lower. Does not alter the front plane (e.g. Music Catalog).
 */
const SectionHeroTitle = ({
  title,
  imageUrl = SECTION_HERO_PREVIEW_IMAGE,
  imageAlt,
  headingLevel = 3,
  titleClassName = "",
  className = "",
  imageOpacity = 0.5,
  imageOpacityMobile,
  imageOpacityUnderside,
  imageOpacityUndersideMobile,
  imageOverlay = "none",
  imageBrightness = 1,
  showBottomBorder = true,
  showImageUnderside = false,
}) => {
  const rootRef = useRef(null);
  const planeRef = useRef(null);
  const undersidePlaneRef = useRef(null);
  const src =
    imageUrl && String(imageUrl).trim()
      ? String(imageUrl).trim()
      : SECTION_HERO_PREVIEW_IMAGE;
  const alt =
    imageAlt || (title ? `${title} hero image` : "Section hero image");
  const HeadingTag = headingLevel === 2 ? "h2" : "h3";
  const titleClasses = [
    headingLevel === 2 ? "section-title" : "sub-title",
    styles.title,
    titleClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const clampOpacity = (value, fallback = 0.5) =>
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(1, Math.max(0, value))
      : fallback;

  const opacity = clampOpacity(imageOpacity, 0.5);
  const opacityMobile = clampOpacity(imageOpacityMobile, opacity);
  const opacityUnderside = clampOpacity(
    imageOpacityUnderside,
    opacity, // default: same as top when not specified
  );
  const opacityUndersideMobile = clampOpacity(
    imageOpacityUndersideMobile,
    opacityUnderside, // default: same as desktop underside when not specified
  );

  const brightness =
    typeof imageBrightness === "number" && Number.isFinite(imageBrightness)
      ? Math.max(0, imageBrightness)
      : 1;

  const overlayKey =
    imageOverlay === true
      ? "medium"
      : imageOverlay === false || imageOverlay == null
        ? "none"
        : String(imageOverlay).toLowerCase();
  const overlayClass = OVERLAY_CLASS[overlayKey] || "";

  const overlayMaskStyle = overlayClass
    ? {
        WebkitMaskImage: `url("${src}")`,
        maskImage: `url("${src}")`,
        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }
    : undefined;

  const imageFilter =
    brightness !== 1 ? { filter: `brightness(${brightness})` } : undefined;

  useEffect(() => {
    const rootEl = rootRef.current;
    const planeEl = planeRef.current;
    const undersideEl = undersidePlaneRef.current;
    if (!rootEl || !planeEl || !src) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Same tilt for front + optional underside plane (underside offset is CSS only)
    const setTilt = (deg) => {
      const t = `rotateX(${deg}deg)`;
      planeEl.style.transform = t;
      if (undersideEl) undersideEl.style.transform = t;
    };

    /*
      Mark heroes near the viewport so CSS can freeze far-away 3D paint
      (iOS Safari process kill on thrash scroll / rotate).
      ROLLBACK: remove nearIo + data-hero-near setAttribute/removeAttribute.
    */
    const setNear = (near) => {
      rootEl.setAttribute("data-hero-near", near ? "true" : "false");
    };
    const nearIo = new IntersectionObserver(
      ([entry]) => {
        setNear(Boolean(entry?.isIntersecting));
      },
      { root: null, rootMargin: "80% 0px", threshold: 0 },
    );
    nearIo.observe(rootEl);
    setNear(true);

    if (reducedMotion.matches) {
      setTilt(SCROLL_TILT_REDUCED_DEG);
      return () => {
        nearIo.disconnect();
        rootEl.removeAttribute("data-hero-near");
      };
    }

    // CSS named view timeline on .root handles tilt when supported
    if (supportsViewTimeline) {
      return () => {
        nearIo.disconnect();
        rootEl.removeAttribute("data-hero-near");
      };
    }

    let rafId = 0;
    let active = false;

    /**
     * Progress from short root position only (matches CSS animation-range
     * entry→cover): low while the title band is still near the bottom of
     * the viewport; higher as it moves toward mid/upper cover.
     */
    const updateTilt = () => {
      const rect = rootEl.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      // Align with “entry at bottom → cover ~55%”: progress 0 when root
      // top is near viewport bottom, 1 when root has moved up through ~mid.
      const start = viewportH; // root top at bottom edge → just entering
      const end = viewportH * 0.35; // root top around upper-mid cover
      const progress = Math.min(
        1,
        Math.max(0, (start - rect.top) / Math.max(start - end, 1)),
      );
      const deg =
        SCROLL_TILT_FROM_DEG +
        progress * (SCROLL_TILT_TO_DEG - SCROLL_TILT_FROM_DEG);
      setTilt(deg);
    };

    const tick = () => {
      updateTilt();
      if (active) rafId = window.requestAnimationFrame(tick);
      else rafId = 0;
    };

    const start = () => {
      if (active) return;
      active = true;
      if (!rafId) rafId = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      active = false;
      updateTilt();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
      },
      { root: null, rootMargin: "40% 0px", threshold: 0 },
    );
    io.observe(rootEl);

    updateTilt();
    const first = rootEl.getBoundingClientRect();
    if (first.bottom > 0 && first.top < (window.innerHeight || 0) * 1.2) {
      start();
    }

    const img = rootEl.querySelector("img");
    if (img && !img.complete) {
      img.addEventListener("load", updateTilt);
    }

    return () => {
      active = false;
      if (rafId) window.cancelAnimationFrame(rafId);
      io.disconnect();
      nearIo.disconnect();
      rootEl.removeAttribute("data-hero-near");
      if (img) img.removeEventListener("load", updateTilt);
    };
  }, [src, showImageUnderside]);

  if (!title && !src) return null;

  const rootClass = [
    styles.root,
    showBottomBorder !== false ? styles["has-bottom-border"] : "",
    showImageUnderside ? styles["has-underside"] : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  /*
    Opacity via CSS custom properties so mobile can override in a media query
    without a matchMedia hook. Desktop / mobile pairs for upper + lower planes.
  */
  const rootStyle = {
    "--section-hero-opacity": String(opacity),
    "--section-hero-opacity-mobile": String(opacityMobile),
    "--section-hero-opacity-underside": String(opacityUnderside),
    "--section-hero-opacity-underside-mobile": String(opacityUndersideMobile),
  };

  /** Media stack for top or underside plane (opacity can differ per plane) */
  const mediaStack = (
    imgAlt,
    { decorative = false, underside = false } = {},
  ) => (
    <div
      className={`${styles["plane-face"]} ${styles["plane-face-front"]} ${styles["media-stack"]}`}
    >
      <figure
        className={`${styles.figure}${underside ? ` ${styles["figure-underside"]}` : ""}`}
      >
        <img
          className={styles.image}
          src={src}
          alt={imgAlt}
          style={imageFilter}
          draggable={decorative ? false : undefined}
        />
      </figure>
      {overlayClass ? (
        <div
          className={`${styles.overlay} ${overlayClass}`}
          style={overlayMaskStyle}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  return (
    <div ref={rootRef} className={rootClass} style={rootStyle}>
      {src ? (
        <div
          className={`${styles["image-wrap"]} ${styles["image-wrap-scroll-shrink"]}`}
        >
          {/* Perspective stage; front plane unchanged when underside is added */}
          <div className={styles["image-perspective"]}>
            {/* Top plane */}
            <div ref={planeRef} className={styles["image-plane"]}>
              {mediaStack(alt)}
            </div>

            {/*
              Underside = sibling plane only. Same scroll animation as front;
              vertical offset is on the wrap so the front plane stays untouched.
            */}
            {showImageUnderside ? (
              <div className={styles["image-plane-underside-wrap"]}>
                <div
                  ref={undersidePlaneRef}
                  className={`${styles["image-plane"]} ${styles["image-plane-underside"]}`}
                  aria-hidden="true"
                >
                  {mediaStack("", {
                    decorative: true,
                    underside: true,
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {title ? (
        <div className={styles["sub-title-background"]}>
          <div className={styles["title-text-background"]}>
            <BrandStripeChrome />
          </div>
          <HeadingTag className={titleClasses}>{title}</HeadingTag>
        </div>
      ) : null}
    </div>
  );
};

export default SectionHeroTitle;
